/** Shared display helpers for credit issue request API rows (public + internal lists). */

export function truncateMiddle(str, headChars = 8, tailChars = 6) {
  const s = str == null ? '' : String(str);
  if (s.length <= headChars + tailChars + 1) return s;
  return `${s.slice(0, headChars)}…${s.slice(-tailChars)}`;
}

/** @param {unknown} req */
export function requestIdFromRow(req) {
  if (!req || typeof req !== 'object') return '';
  const id = 'request_id' in req ? req.request_id : 'id' in req ? req.id : null;
  return id != null && String(id).trim() !== '' ? String(id).trim() : '';
}

/** @param {unknown} req */
export function amountMinorFromRow(req) {
  if (!req || typeof req !== 'object') return null;
  const v = 'amount_minor' in req ? req.amount_minor : 'amount' in req ? req.amount : null;
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** @param {unknown} req */
export function rrnFromRow(req) {
  if (!req || typeof req !== 'object') return '—';
  const ref = 'reference_id' in req ? req.reference_id : 'rrn' in req ? req.rrn : null;
  if (ref == null || String(ref).trim() === '') return '—';
  return String(ref);
}

/** @param {unknown} req */
export function statusFromRow(req) {
  if (!req || typeof req !== 'object') return '';
  const s = 'status' in req && req.status != null ? String(req.status).trim().toUpperCase() : '';
  return s;
}

/** @param {unknown} req */
export function requestedByFromRow(req) {
  if (!req || typeof req !== 'object') return '—';
  const v =
    'requested_by' in req
      ? req.requested_by
      : 'requested_by_uid' in req
        ? req.requested_by_uid
        : 'created_by' in req
          ? req.created_by
          : null;
  if (v == null || String(v).trim() === '') return '—';
  return String(v);
}

/** @param {unknown} req */
export function createdMsFromRow(req) {
  if (!req || typeof req !== 'object') return 0;
  const raw =
    'created_at_ms' in req
      ? req.created_at_ms
      : 'created_at' in req
        ? req.created_at
        : 'requested_at_ms' in req
          ? req.requested_at_ms
          : null;
  if (raw == null) return 0;
  let ms;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    ms = raw < 1e11 ? raw * 1000 : raw;
  } else if (typeof raw === 'string' && /^\d+$/.test(raw.trim())) {
    const n = Number(raw.trim());
    ms = n < 1e11 ? n * 1000 : n;
  } else {
    const d = new Date(String(raw));
    ms = d.getTime();
  }
  return Number.isFinite(ms) ? ms : 0;
}

/** @param {unknown} req */
export function createdFromRow(req) {
  const ms = createdMsFromRow(req);
  if (!ms) return '—';
  try {
    return new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(ms));
  } catch {
    return '—';
  }
}

/** @param {unknown} metadata */
export function metadataAsObject(metadata) {
  if (metadata == null) return null;
  if (typeof metadata === 'string') {
    const t = metadata.trim();
    if (t === '') return null;
    try {
      const p = JSON.parse(t);
      return typeof p === 'object' && p !== null && !Array.isArray(p) ? p : null;
    } catch {
      return null;
    }
  }
  if (typeof metadata === 'object' && !Array.isArray(metadata)) return metadata;
  return null;
}

/** Shallow-merge `metadata` and `review_metadata`; review keys take precedence. */
export function mergedMetadataFromRow(req) {
  if (!req || typeof req !== 'object') return null;
  const base = metadataAsObject('metadata' in req ? req.metadata : null);
  const review = metadataAsObject('review_metadata' in req ? req.review_metadata : null);
  if (!base && !review) return null;
  return { ...(base ?? {}), ...(review ?? {}) };
}

function stringFromMeta(meta, key) {
  if (!meta || !(key in meta)) return '';
  const v = meta[key];
  if (v == null) return '';
  const t = typeof v === 'string' ? v.trim() : String(v).trim();
  return t;
}

/** @param {unknown} req */
export function requestorNoteFromRow(req) {
  return stringFromMeta(mergedMetadataFromRow(req), 'requestor_note');
}

/** @param {unknown} req */
export function requestorNotePreview(req) {
  const note = requestorNoteFromRow(req);
  if (!note) return '—';
  return note.length > 120 ? `${note.slice(0, 117)}…` : note;
}

/** @param {unknown} req — reads `notes` from merged metadata + review_metadata */
export function metadataNotesFromRow(req) {
  return stringFromMeta(mergedMetadataFromRow(req), 'notes');
}

/** @param {unknown} req */
export function metadataNotesPreview(req) {
  const note = metadataNotesFromRow(req);
  if (!note) return '—';
  return note.length > 120 ? `${note.slice(0, 117)}…` : note;
}

/** @param {unknown} req — reads `notes` from merged metadata (review_metadata takes precedence) */
export function reviewNotesFromRow(req) {
  return stringFromMeta(mergedMetadataFromRow(req), 'notes');
}

/** @param {unknown} req */
export function reviewNotesPreview(req) {
  const note = reviewNotesFromRow(req);
  if (!note) return '—';
  return note.length > 120 ? `${note.slice(0, 117)}…` : note;
}

/** Epoch ms from merged `repayment_date_ms` (review_metadata takes precedence). Returns 0 when absent/invalid. */
export function repaymentDateMsFromRow(req) {
  const meta = mergedMetadataFromRow(req);
  if (!meta) return 0;
  const raw = 'repayment_date_ms' in meta ? meta.repayment_date_ms : null;
  if (raw == null) return 0;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Formatted repayment date string for display (merged metadata, review takes precedence). */
export function repaymentDateFromRow(req) {
  const ms = repaymentDateMsFromRow(req);
  if (!ms) return '—';
  try {
    return new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(ms));
  } catch {
    return '—';
  }
}
